// Centralised SEO configuration & JSON-LD schema builders for WristCoin.
// Single source of truth for titles, descriptions, canonical URLs and structured data.
import { pricingPlans } from "../data/mockData";

export const SITE = {
  domain: "https://mywristcoin.com",
  name: "WristCoin",
  defaultTitle:
    "WristCoin | RFID Cashless Payments & POS for Hospitality & Events",
  defaultDescription:
    "WristCoin is an RFID wristband cashless payment and POS platform for spas, resorts, beach clubs, parks, festivals and events. Fast offline tap-to-pay, loyalty, access control and real-time insights.",
  defaultImage: "/images/landing_page_hero.png",
  logo: "/images/wristcoin_logo.png",
  twitter: "@mywristcoin",
  email: "info@mywristcoin.com",
  supportEmail: "support@mywristcoin.com",
};

export const SOCIAL_PROFILES = [
  "https://www.youtube.com/@wristcoin",
  "https://www.linkedin.com/company/wristcoin/",
  "https://www.instagram.com/mywristcoin/",
  "https://www.facebook.com/mywristcoin",
  "https://x.com/mywristcoin",
];

// Verified pixel dimensions for every image used as a social share image
// (og:image / twitter:image). Measured directly from the source files —
// see Step 5 social metadata audit. Keep in sync if an image is replaced.
export const IMAGE_DIMENSIONS = {
  "/images/landing_page_hero.png": { width: 800, height: 533 },
  "/images/apps/apps-hero.png": { width: 1090, height: 554 },
  "/images/case-studies/hollerbach-card.png": { width: 1200, height: 661 },
  "/images/case-studies/la-marqueta.jpg": { width: 800, height: 533 },
  "/images/case-studies/arenia-casino.jpg": { width: 1430, height: 1104 },
  "/images/case-studies/st-francis.jpg": { width: 1831, height: 593 },
  "/images/clients/Oasiz Madrid.png": { width: 1200, height: 800 },
  "/images/case-studies/behoteles.png": { width: 686, height: 416 },
  "/images/case-studies/payments-canada-preview.png": { width: 332, height: 252 },
  "/images/case-studies/aniwa-gathering.jpg": { width: 1831, height: 1221 },
  "/images/blog/hollerbach.png": { width: 1200, height: 661 },
  "/images/blog/oasiz.png": { width: 1200, height: 800 },
  "/images/blog/wristbands-better.png": { width: 800, height: 800 },
  "/images/blog/convenient-top-ups.png": { width: 960, height: 540 },
  "/images/blog/nfc-anti-tearing.png": { width: 800, height: 800 },
  "/images/blog/save-money.png": { width: 500, height: 500 },
};

// Returns verified {width, height} for a given image path, falling back to
// the default hero's dimensions if the path isn't in the map (should not
// normally happen, since every og:image used resolves to a known file).
export const imageDimensions = (path) =>
  IMAGE_DIMENSIONS[path] || IMAGE_DIMENSIONS["/images/landing_page_hero.png"];

// Absolute URL helper
export const abs = (u) => {
  if (!u) return SITE.domain;
  if (u.startsWith("http")) return u;
  return encodeURI(`${SITE.domain}${u.startsWith("/") ? "" : "/"}${u}`);
};

// Build a canonical URL from a path (root has no trailing slash)
export const canonical = (path = "/") => {
  if (!path || path === "/") return `${SITE.domain}/`;
  const clean = "/" + String(path).replace(/^\/+|\/+$/g, "");
  return `${SITE.domain}${clean}`;
};

// Normalise a blog slug (data slugs contain leading/trailing slashes)
export const blogSlug = (slug = "") => String(slug).replace(/^\/+|\/+$/g, "");
export const blogPath = (slug = "") => `/blog/${blogSlug(slug)}`;

// ---------------------------------------------------------------------------
// Per-page SEO metadata (static + case study pages)
// ---------------------------------------------------------------------------
export const PAGE_SEO = {
  "/": {
    title: "RFID Wristband Cashless Payments & POS | WristCoin",
    description:
      "RFID wristband cashless payments and POS for spas, resorts, venues, festivals and events. Works offline, with loyalty, access control and real-time reporting.",
    path: "/",
    image: SITE.defaultImage,
    type: "website",
  },
  "/apps": {
    title: "Guest & Attendee App | WristCoin Cashless Payments",
    description:
      "Download the free WristCoin guest/attendee app from the App Store or Google Play to check your wristband balance, load money and view transactions at venues and events.",
    path: "/apps",
    image: "/images/apps/apps-hero.png",
    type: "website",
  },
  "/case-studies": {
    title: "Cashless Payment Case Studies | WristCoin",
    description:
      "See how festivals, a nightclub, casinos, a shopping mall, a school gala and a payments conference use WristCoin RFID cashless payments to cut lines and track sales.",
    path: "/case-studies",
    image: SITE.defaultImage,
    type: "website",
  },
  "/support": {
    title: "Customer Support | WristCoin",
    description:
      "Get help with WristCoin RFID cashless payments and POS. Contact WristCoin customer support for setup, troubleshooting and account questions.",
    path: "/support",
    image: SITE.defaultImage,
    type: "website",
  },
  "/privacy-policy": {
    title: "Privacy Policy | WristCoin",
    description:
      "Read the WristCoin privacy policy to learn how we collect, use and protect personal data across our cashless payment platform.",
    path: "/privacy-policy",
    image: SITE.defaultImage,
    type: "website",
  },
  "/terms-conditions": {
    title: "Terms & Conditions | WristCoin",
    description:
      "Read the WristCoin terms and conditions governing the use of our RFID cashless payment and POS platform.",
    path: "/terms-conditions",
    image: SITE.defaultImage,
    type: "website",
  },
  "/terms-and-conditions": {
    title: "Terms and Conditions | WristCoin",
    description:
      "The Wristcoin Terms and Conditions governing the products and services provided by Papyrus Electronics Inc. to customers under an Order.",
    path: "/terms-and-conditions",
    image: SITE.defaultImage,
    type: "website",
  },
  "/blog": {
    title: "Behind the Tap: Cashless Payments Blog | WristCoin",
    description:
      "Insights, product updates and guides on RFID cashless payments, NFC wristbands and guest experience from the WristCoin team.",
    path: "/blog",
    image: SITE.defaultImage,
    type: "website",
  },
  "/case-studies/hollerbach-oktoberfest": {
    title: "Hollerbach: Self-Serve Top Ups & Reporting Solutions | WristCoin",
    description:
      "How Hollerbach's Oktoberfest in Florida used WristCoin RFID wristbands to cut lines, track sales accurately and grow revenue at its popular annual event.",
    path: "/case-studies/hollerbach-oktoberfest",
    image: "/images/case-studies/hollerbach-card.png",
    type: "article",
  },
  "/case-studies/la-marqueta": {
    title: "La Marqueta Nightclub NFC Wristband Case Study | WristCoin",
    description:
      "La Marqueta in San Juan replaced paper drink tickets with WristCoin NFC wristbands, achieving a 23% revenue increase and reduced ticket misuse.",
    path: "/case-studies/la-marqueta",
    image: "/images/case-studies/la-marqueta.jpg",
    type: "article",
  },
  "/case-studies/arenia-casinos": {
    title: "Arenia Casinos: VIP Loyalty Rewards Case Study | WristCoin",
    description:
      "Arenia Casinos eliminated VIP voucher misuse across three locations with WristCoin RFID loyalty rewards and real-time reporting dashboards.",
    path: "/case-studies/arenia-casinos",
    image: "/images/case-studies/arenia-casino.jpg",
    type: "article",
  },
  "/case-studies/st-francis-donor-event": {
    title: "St. Francis School Cashless Donor Gala Case Study | WristCoin",
    description:
      "St. Francis Episcopal School ran a fully cashless donor gala with nearly 50 WristCoin terminals while keeping costs viable and increasing attendance.",
    path: "/case-studies/st-francis-donor-event",
    image: "/images/case-studies/st-francis.jpg",
    type: "article",
  },
  "/case-studies/oasiz-mall": {
    title:
      "Revolutionizing Retail: How Oasiz Mall, Spain's Largest Shopping Centre, Successfully Integrated WristCoin | WristCoin",
    description:
      "Oasiz Mall, Spain's largest shopping centre, deployed WristCoin closed-loop cashless wristbands with direct banking integration, running since 2021.",
    path: "/case-studies/oasiz-mall",
    image: "/images/clients/Oasiz Madrid.png",
    type: "article",
  },
  "/case-studies/behoteles": {
    title: "BeHoteles Tulum: Offline Cashless Bar Case Study | WristCoin",
    description:
      "BeHoteles delivered flawless offline cashless bar service for thousands of guests in a remote Tulum jungle venue with no WiFi using WristCoin.",
    path: "/case-studies/behoteles",
    image: "/images/case-studies/behoteles.png",
    type: "article",
  },
  "/case-studies/payments-canada": {
    title: "Payments Canada: Electronic Drink Tickets | WristCoin",
    description:
      "Payments Canada replaced paper drink tickets with WristCoin pre-loaded electronic tickets and accurate real-time tallies at its annual conference.",
    path: "/case-studies/payments-canada",
    image: "/images/case-studies/payments-canada-preview.png",
    type: "article",
  },
  "/case-studies/aniwa-gathering": {
    title: "Aniwa Gathering: Offline Cashless Festival Case Study | WristCoin",
    description:
      "Aniwa Gathering ran a fully cashless event in remote Big Bear wilderness with WristCoin offline terminals and credit-card top-up kiosks.",
    path: "/case-studies/aniwa-gathering",
    image: "/images/case-studies/aniwa-gathering.jpg",
    type: "article",
  },
};

export const getPageSeo = (path) => PAGE_SEO[path] || null;

// Blog post SEO title/description overrides (visible title and excerpt unchanged).
export const BLOG_SEO = {
  "new-guest-management-features": {
    title: "New Guest Management Features for Events | WristCoin",
    description:
      "WristCoin guest management for events: on-demand guest registration, detailed transaction reports and importing guest lists onto wristbands.",
  },
  "new-vendor-management-features": {
    title: "New Vendor Management Features for Cashless Events | WristCoin",
    description:
      "Vendor tools in WristCoin: orders sent to back-of-house, customizable menu items, sales tax tracking, automatic gratuities and staff access controls.",
  },
  "new-guest-experience-features": {
    description:
      "New WristCoin guest experience features: loyalty and rewards point wallet, preloaded wristbands, self-serve top-ups, venue entry access and fast transactions.",
  },
  "what-makes-our-wristbands-better": {
    title: "What Makes WristCoin Wristbands Better? | WristCoin",
    description:
      "Why WristCoin wristbands stand out: secure offline transactions, speed, reusability, reduced lead time and self-serve top-ups for venues and events.",
  },
  "closed-loop-cashless-vs-open-loop-payments-for": {
    title: "Closed-Loop vs Open-Loop Cashless Payments for Festivals | WristCoin",
    description:
      "Open-loop vs closed-loop payments explained, and why closed-loop cashless suits festivals: faster transactions, lower card fees and a better attendee experience.",
  },
  "convenient-self-serve-top-ups": {
    title: "Self-Serve Top-Ups for Cashless Events | WristCoin",
    description:
      "How WristCoin self-serve top-ups work for attendees: no app or login, no personal information required, lost wristband recovery and automatic refunds.",
  },
  "cashless-event-payments-straight-to-your-account": {
    description:
      "How WristCoin handles the money at a cashless event: simple pricing with no per-transaction fees, and payments that go straight to your account.",
  },
  "cashless-event-badges-can-replace-drink-tickets": {
    description:
      "Cashless event badges can replace paper drink tickets at golf events, tournaments and conferences, with better inventory tracking and billing.",
  },
  "nfc-tag-and-chips-for-cashless-one-anti-tearing": {
    title: "NFC Chips for Cashless Payments, Part 1: Anti-Tearing | WristCoin",
    description:
      "Why NFC memory tearing matters for offline cashless wallets, and how anti-tearing chips protect wristband balances. Part one of WristCoin's NFC series.",
  },
  "nfc-tag-and-chips-for-cashless-applications-two": {
    title: "NFC Chip Security, Part 2: Passwords vs Authentication | WristCoin",
    description:
      "NFC chip security for offline cashless wallets: no security vs password protection vs authentication. Part two of WristCoin's NFC series.",
    image: "/images/blog/nfc-anti-tearing.png",
  },
  "nfc-tag-and-chips-for-cashless-applications-three": {
    title: "NFC Chips for Cashless Payments, Part 3: Advanced Security | WristCoin",
    description:
      "Choosing NFC chips for long-term, high-value cashless use, and the additional security features that help prevent fraud. Part three of WristCoin's NFC series.",
    image: "/images/blog/nfc-anti-tearing.png",
  },
  "do-it-yourself-cashless-payments-even-without-wifi": {
    title: "DIY Cashless Event Payments, Even Without WiFi | WristCoin",
    description:
      "How event organizers can run WristCoin cashless payments themselves, without large staff, bulky equipment or WiFi.",
  },
  "what-about-agility-for-you-venue": {
    title: "Cashless Payments for Restaurants, Bars and Nightclubs | WristCoin",
    description:
      "How WristCoin helps restaurants, bars, nightclubs and other venues with payments and stronger guest relationships.",
  },
  "why-is-wristcoin-cashless-payments-for-your-resort": {
    title: "Cashless Payments and Purchase Tracking for Resorts | WristCoin",
    description:
      "How resorts use WristCoin to upsell, track purchases, set spending limits for children, accept contactless tips and save costs.",
  },
  "how-hospitality-tourism-is-going-contactless": {
    title: "Contactless Tourism: Closed-Loop Cashless for Hospitality | WristCoin",
    description:
      "Why closed-loop contactless payments matter for hospitality and tourism, and how they support guest experience.",
  },
  "wristcoin-can-save-money-for-an-event-organizer": {
    title: "How WristCoin Saves Event Organizers Money | WristCoin",
    description:
      "A comparison of event payment options and how an affordable cashless system can reduce costs for event organizers with limited budgets.",
  },
};

export const getBlogSeo = (article) => {
  const o = BLOG_SEO[blogSlug(article.slug)] || {};
  return {
    title: o.title || `${article.title} | WristCoin`,
    description: o.description || article.excerpt,
    // Optional social-image override, used only when the post's own image
    // is the generic placeholder but a more relevant existing image exists
    // (e.g. reusing part 1's image for other parts of the same series).
    // This affects only the og:image/twitter:image meta tags, not the
    // visible page hero or the JSON-LD article image.
    image: o.image,
  };
};

// ---------------------------------------------------------------------------
// JSON-LD entity graph (stable @id identifiers)
// ---------------------------------------------------------------------------
export const IDS = {
  organization: `${SITE.domain}/#organization`,
  website: `${SITE.domain}/#website`,
  logo: `${SITE.domain}/#logo`,
  service: `${SITE.domain}/#service`,
  pricing: `${SITE.domain}/#pricing`,
  blog: `${SITE.domain}/blog#blog`,
  app: `${SITE.domain}/apps#app`,
};

const ref = (id) => ({ "@id": id });

// Minimal stubs so cross-page references resolve within each page's graph.
const blogStub = () => ({ "@type": "Blog", "@id": IDS.blog, name: "Behind the Tap", url: `${SITE.domain}/blog` });
const serviceStub = () => ({
  "@type": "Service",
  "@id": IDS.service,
  name: "WristCoin RFID Cashless Payments & POS",
  url: `${SITE.domain}/`,
});

const PLACEHOLDER_IMAGE = "/images/blog/placeholder.png";
export const isPlaceholderImage = (img) => !img || img === PLACEHOLDER_IMAGE;

const COLLECTION_PATHS = ["/blog", "/case-studies"];

export const orgSchema = () => ({
  "@type": "Organization",
  "@id": IDS.organization,
  name: "WristCoin",
  legalName: "PAPYRUS ELECTRONICS INC",
  foundingDate: "2011",
  url: `${SITE.domain}/`,
  description:
    "WristCoin is the public-facing brand of PAPYRUS ELECTRONICS INC, providing RFID wristband cashless payment and POS hardware and software for hospitality and events worldwide.",
  logo: {
    "@type": "ImageObject",
    "@id": IDS.logo,
    url: abs(SITE.logo),
    contentUrl: abs(SITE.logo),
    caption: "WristCoin",
  },
  image: ref(IDS.logo),
  email: SITE.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: "108 College St Suite W754",
    addressLocality: "Toronto",
    postalCode: "M5G 0C6",
    addressCountry: "CA",
  },
  contactPoint: [
    { "@type": "ContactPoint", contactType: "general inquiries", email: SITE.email },
    { "@type": "ContactPoint", contactType: "customer support", email: SITE.supportEmail },
  ],
  sameAs: SOCIAL_PROFILES,
});

export const websiteSchema = () => ({
  "@type": "WebSite",
  "@id": IDS.website,
  name: "WristCoin",
  url: `${SITE.domain}/`,
  inLanguage: "en",
  publisher: ref(IDS.organization),
});

export const webPageSchema = ({ path, title, description, image, breadcrumb }) => {
  const url = canonical(path);
  const clean = !path || path === "/" ? "/" : "/" + String(path).replace(/^\/+|\/+$/g, "");
  return {
    "@type": COLLECTION_PATHS.includes(clean) ? "CollectionPage" : "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: "en",
    isPartOf: ref(IDS.website),
    ...(clean === "/" ? { about: ref(IDS.organization) } : {}),
    ...(COLLECTION_PATHS.includes(clean) ? { mainEntity: ref(`${url}#itemlist`) } : {}),
    ...(image && image !== SITE.defaultImage ? { primaryImageOfPage: { "@type": "ImageObject", url: abs(image) } } : {}),
    ...(breadcrumb ? { breadcrumb: ref(`${url}#breadcrumb`) } : {}),
  };
};

const priceValue = (p) => Number(String(p).replace(/[^0-9.]/g, ""));

export const serviceSchema = () => ({
  "@type": "Service",
  "@id": IDS.service,
  name: "WristCoin RFID Cashless Payments & POS",
  serviceType: "RFID cashless payment and POS platform",
  url: `${SITE.domain}/`,
  description:
    "Cashless payments and POS platform supplying RFID/NFC wristband hardware and software for spas, resorts, beach clubs, parks, venues, festivals and events, including cashless payments (open and closed loop), loyalty and membership, access and experience control, and guest profiles and insights.",
  provider: ref(IDS.organization),
  areaServed: "Worldwide",
  audience: {
    "@type": "BusinessAudience",
    audienceType: "Hospitality venues and event organizers",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    "@id": IDS.pricing,
    name: "Spa, Hospitality, and Venue Pricing",
    description:
      "Starting prices per terminal per month. Event and festival pricing is quoted separately based on scale and requirements.",
    itemListElement: pricingPlans.map((plan) => {
      const offer = {
        "@type": "Offer",
        name: plan.name,
        url: `${SITE.domain}/#pricing`,
        offeredBy: ref(IDS.organization),
      };
      if (!plan.price.startsWith("$")) {
        return { ...offer, description: "Contact for pricing." };
      }
      return {
        ...offer,
        description: `Starting at ${plan.price} USD ${plan.period}.`,
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          minPrice: priceValue(plan.price),
          priceCurrency: "USD",
          unitText: "per terminal per month",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: "terminal" },
          billingDuration: "P1M",
        },
      };
    }),
  },
});

export const breadcrumbSchema = (items = []) => {
  const last = items[items.length - 1];
  return {
    "@type": "BreadcrumbList",
    "@id": `${canonical(last ? last.path : "/")}#breadcrumb`,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: canonical(it.path),
    })),
  };
};

export const blogSchema = () => ({
  "@type": "Blog",
  "@id": IDS.blog,
  name: "Behind the Tap",
  url: canonical("/blog"),
  inLanguage: "en",
  publisher: ref(IDS.organization),
  isPartOf: ref(IDS.website),
  mainEntityOfPage: ref(`${canonical("/blog")}#webpage`),
});

const isoDate = (d) => {
  const t = new Date(d);
  if (isNaN(t)) return undefined;
  const pad = (n) => String(n).padStart(2, "0");
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
};

export const articleSchema = (article) => {
  const url = canonical(blogPath(article.slug));
  const sections = (article.categories || []).filter((c) => c !== "Uncategorized");
  return {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: article.title,
    description: getBlogSeo(article).description,
    url,
    datePublished: isoDate(article.date),
    ...(isPlaceholderImage(article.image) ? {} : { image: abs(article.image) }),
    ...(sections.length ? { articleSection: sections } : {}),
    inLanguage: "en",
    author: ref(IDS.organization),
    publisher: ref(IDS.organization),
    isPartOf: blogStub(),
    mainEntityOfPage: ref(`${url}#webpage`),
  };
};

// Case studies: headline = visible H1, client = organisation named on the page.
export const CASE_STUDIES = {
  "/case-studies/hollerbach-oktoberfest": {
    headline: "Hollerbach: Self-Serve Top Ups & Reporting Solutions",
    client: "Hollerbach's Oktoberfest",
  },
  "/case-studies/la-marqueta": {
    headline: "La Marqueta, Puerto Rico: Agility and Improved User Experience with NFC Wristbands",
    client: "La Marqueta",
  },
  "/case-studies/arenia-casinos": {
    headline: "Arenia Casinos: Transforming VIP Loyalty Rewards with WristCoin",
    client: "Arenia Casinos",
  },
  "/case-studies/st-francis-donor-event": {
    headline: "St. Francis: Non-profit executes a Successful Donor Event",
    client: "St. Francis Episcopal School",
  },
  "/case-studies/oasiz-mall": {
    headline:
      "Revolutionizing Retail: How Oasiz Mall, Spain's Largest Shopping Centre, Successfully Integrated WristCoin",
    client: "Oasiz Mall",
  },
  "/case-studies/behoteles": {
    headline: "BeHoteles: A No WIFI Solution",
    client: "BeHoteles",
  },
  "/case-studies/payments-canada": {
    headline: "Payments Canada: Electronic Drink Tickets",
    client: "Payments Canada",
  },
  "/case-studies/aniwa-gathering": {
    headline: "Aniwa Gathering: A Cashless Event Triumph in the Heart of Nature",
    client: "Aniwa Gathering",
  },
};

export const caseStudySchema = (path) => {
  const url = canonical(path);
  const cs = CASE_STUDIES[path];
  const page = PAGE_SEO[path];
  return {
    "@type": "Article",
    "@id": `${url}#article`,
    headline: cs.headline,
    description: page.description,
    url,
    image: abs(page.image),
    articleSection: "Case Studies",
    inLanguage: "en",
    author: ref(IDS.organization),
    publisher: ref(IDS.organization),
    about: serviceStub(),
    mentions: { "@type": "Organization", name: cs.client },
    mainEntityOfPage: ref(`${url}#webpage`),
  };
};

export const appSchema = () => ({
  "@type": "MobileApplication",
  "@id": IDS.app,
  name: "WristCoin",
  alternateName: "WristCoin Guest/Attendee App",
  url: canonical("/apps"),
  description:
    "Free WristCoin guest/attendee app to check your wristband balance, load money and view transactions.",
  featureList: ["Check Balance", "Load Money", "View Transactions"],
  image: abs("/images/apps/apps-hero.png"),
  installUrl: [
    "https://apps.apple.com/us/app/wristcoin/id1462360007",
    "https://play.google.com/store/apps/details?id=com.mywristcoin.balancechecker",
  ],
  offers: { "@type": "Offer", price: 0, priceCurrency: "USD" },
  publisher: ref(IDS.organization),
  mainEntityOfPage: ref(`${canonical("/apps")}#webpage`),
});

export const itemListSchema = (path, items) => ({
  "@type": "ItemList",
  "@id": `${canonical(path)}#itemlist`,
  numberOfItems: items.length,
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: canonical(it.path),
    name: it.name,
  })),
});
